import type { ComponentProps, FC } from "react";
import { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { Table } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, cpcFilter } from "app/filters/ngFilters";
import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { getShareValue, getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import DurationServiceInstance from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { keywordGroupsTransforms } from "app/services/keyword-groups";

import { useSearchAdsEndpoints } from "../../searchAdsEndpoints";
import { getKeywordsCountTableColumnsConfig } from "../searchAdsTableColumnsConfig";
import {
    SHOPPING,
    TABLE_COLUMN_CLICKS_SHARE,
    TEXT,
} from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";
import { PAGE_SIZE } from "../searchAdsTableSharedConsts";
import type {
    IExpandedData,
    IExpandedDataRecord,
    IKeywordsCountTableProps,
    ITransformExpendedDataObject,
} from "app/common/keyword-research/website-search-ads/searchAdsTableTypes";
import { CloseButtonContainer, HeaderRow } from "../StyledComponents";

const transformData = (response: IExpandedDataRecord): ITransformExpendedDataObject => {
    const transformedData = response.Data.map((record: IExpandedData, index: number) => {
        const { Clicks, ClicksShare, ClicksChange, AvgVolume, Keyword, Volume, Cpc, ...rest } = record;
        const totalClicks = getVolumeValue(Clicks);
        return {
            key: index,
            keyword: Keyword,
            clicksShare: { totalClicks, value: getShareValue(ClicksShare) },
            clicksChange: ClicksChange,
            volume: abbrNumberFilter()(Volume),
            avgVolume: abbrNumberFilter()(AvgVolume),
            cpc: cpcFilter()(Cpc),
        };
    }).filter(Boolean);
    // @ts-expect-error FIXME: Types of property 'avgVolume' are incompatible.
    return { data: transformedData, totalCount: response.TotalCount };
};
const initialTableSort: ISortProps = { asc: false, sort: TABLE_COLUMN_CLICKS_SHARE };
export const KeywordsCountTable: FC<IKeywordsCountTableProps> = ({ onClick, adId }) => {
    const endpoints = useSearchAdsEndpoints();
    const { country, webSource, duration, key, keyword, selectedPageTab, pageFilter, selectedSite } =
        useSearchAdvertisingDataAndParams();
    const { from, to, isWindow } = DurationServiceInstance.getDurationData(duration).forAPI;
    const [currentPage, setCurrentPage] = useState(1);
    const [sort, setSort] = useState<ISortProps>({
        sort: initialTableSort.sort,
        asc: initialTableSort.asc,
    });
    const getSelectedSite = (site: string) => site ?? key?.split(",")[0];
    const isCompare = chosenSites.isCompare();
    const selectedKey = isCompare ? getSelectedSite(selectedSite) : key;
    const apiParams = {
        country,
        webSource,
        to,
        from,
        isWindow,
        sort: sort.sort,
        asc: sort.asc,
        AdId: adId,
        pageFilter,
        keys: key ? selectedKey : keywordGroupsTransforms.getGroupIdFromUrlParam(keyword),
        adType: selectedPageTab === EAdsTabs[EAdsTabs.Text] ? TEXT : SHOPPING,
        page: currentPage,
    };
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            endpoints.keywordsDrillDown,
            to,
            from,
            duration,
            webSource,
            isWindow,
            key,
            country,
            selectedPageTab,
            sort.asc,
            sort.sort,
            pageFilter,
            currentPage,
            adId,
        ],
        queryFn: async () => DefaultFetchService.getInstance().get(endpoints.keywordsDrillDown, apiParams),
        select: (response: IExpandedDataRecord) => transformData(response),
    });

    const onSort: ComponentProps<typeof Table<(typeof data.data)[number]>>["onSort"] = (sorter) => {
        const isAsc = sorter.order === "ascend";
        setSort({
            sort: sorter.columnKey as string,
            asc: isAsc,
        });
        setCurrentPage(1);
    };

    const noDataObject = isError
        ? {
              title: translate("keyword.research.search.ads.table.error.title"),
              subtitle: translate("keyword.research.search.ads.table.error.subtitle"),
          }
        : {
              title: translate("keyword.research.search.ads.table.no.data.title"),
              subtitle: translate("keyword.research.search.ads.table.no.data.subtitle"),
          };
    return (
        <>
            <HeaderRow>
                <CloseButtonContainer>
                    <IconButton
                        type="flat"
                        iconName="clear"
                        iconSize="sm"
                        onClick={onClick}
                    />
                </CloseButtonContainer>
            </HeaderRow>
            <Table<(typeof data.data)[number]>
                data-automation="KeywordsCountTable"
                columnConfig={getKeywordsCountTableColumnsConfig(data?.totalCount ?? 0, currentPage, sort) as any}
                data={data?.data ?? []}
                isLoading={isLoading}
                loaderRowCount={5}
                noData={noDataObject}
                pagination={{
                    pageSize: PAGE_SIZE,
                    total: data?.totalCount ?? 0,
                    current: currentPage,
                    onChange: (_, __, newPage) => setCurrentPage(newPage),
                }}
                onSort={onSort}
            />
        </>
    );
};
